import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import {
  EventNoteOutlined,
  ExitToAppRounded,
  ExpandLessRounded,
  ExpandMoreRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SIDEBAR_ICON_COLOR, SIDEBAR_TEXT_COLOR } from 'lib/sidebarNavUtils';

const useStyles = makeStyles((theme) => ({
  menu: { paddingLeft: theme.spacing(1) },
  itemMenu: { paddingLeft: theme.spacing(2) },
  subItemMenu: { paddingLeft: theme.spacing(3) },
  selectedItem: {
    '&.Mui-selected': { backgroundColor: 'transparent' }
  },
  icon: {
    minWidth: 32,
    color: SIDEBAR_ICON_COLOR
  },
  activeIcon: { color: theme.palette.primary.main },
  subtleTitle: {
    fontWeight: 500,
    color: SIDEBAR_TEXT_COLOR
  },
  activeTitle: { color: theme.palette.primary.main },
  itemMenuFade: {
    animation: `$fadeInSlide 600ms ease-out`,
    position: 'relative',
    zIndex: 5,
    backgroundColor: theme.palette.common.white,
    borderRadius: 6,
    boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    padding: '4px 0',
    animationFillMode: 'forwards',
    transition: 'box-shadow 600ms ease, background-color 600ms ease'
  },
  '@keyframes fadeInSlide': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      backgroundColor: 'transparent'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      backgroundColor: theme.palette.common.white
    }
  },
  disabledItem: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  emptyMessage: {
    paddingLeft: theme.spacing(4),
    opacity: 0.6,
    fontStyle: 'italic'
  }
}));

/* Menu fixo do módulo Partner */
const menuConfig = [
  {
    key: 'agenda',
    label: 'Agenda Escalas',
    icon: EventNoteOutlined,
    to: '/partner/agendaescalafornecedor'
  },
  {
    key: 'logout',
    label: 'Sair',
    icon: ExitToAppRounded,
    to: '/logout'
  }
];

/* Monta a árvore de itens, estilo Delphi */
function buildMenuDelphiStyle(items, hasPerm) {
  return (items || []).map((item) => {
    const hasChildren = !!(item.children && item.children.length > 0);
    const isLeaf = !hasChildren;

    if (isLeaf) {
      const allowed = !item.permCode || hasPerm(item.permCode);

      return {
        ...item,
        visible: true,
        disabled: !allowed,
        children: []
      };
    }

    const children = buildMenuDelphiStyle(item.children || [], hasPerm);

    return {
      ...item,
      visible: true,
      disabled: false,
      children
    };
  });
};

/* Menu lateral do módulo Partner */
const Sidebar = () => {
  const classes = useStyles();
  const location = useLocation();
  const hasPerm = () => true;
  const loading = false;
  const [openMap, setOpenMap] = useState({});
  const setOpen = (key, value) => setOpenMap((prev) => ({ ...prev, [key]: value }));
  const isOpen = (key) => !!openMap[key];
  const isPartnerFixedMenu = true; // menu fixo (bridge), sem permissões reais atualmente

  const finalMenu = useMemo(() => {
    if (isPartnerFixedMenu) {
      return buildMenuDelphiStyle(menuConfig, hasPerm);
    }

    return buildMenuDelphiStyle(menuConfig, hasPerm);
  }, [hasPerm]);

  // verifica se a rota atual está dentro do item (destaca o menu pai)
  const hasActiveDescendant = (item, pathname) => {
    if (!item.children || item.children.length === 0) {
      return item.to === pathname;
    }

    return item.children.some((child) => hasActiveDescendant(child, pathname));
  };

  const renderItems = (items, level = 0) => {
    return items.map((item) => {
      if (!item.visible) return null;

      const Icon = item.icon;
      const hasChildren = item.children && item.children.length > 0;
      const levelClass = level === 0 ? classes.menu : level === 1 ? classes.itemMenu : classes.subItemMenu;

      if (!hasChildren) {
        const isActive = location.pathname === item.to;

        const commonProps = {
          key: item.key,
          className: clsx(
            levelClass,
            item.disabled && classes.disabledItem
          )
        };

        if (item.disabled) {
          return (
            <ListItem
              {...commonProps}
              button={false}
            >
              <ListItemIcon className={classes.icon}>
                {Icon ? <Icon /> : null}
              </ListItemIcon>

              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ className: classes.subtleTitle }}
              />
            </ListItem>
          );
        }

        return (
          <ListItem
            {...commonProps}
            className={clsx(
              commonProps.className,
              classes.selectedItem
            )}
            button
            component={Link}
            to={item.to}
            selected={isActive}
          >
            <ListItemIcon className={clsx(
              classes.icon,
              isActive && classes.activeIcon
            )}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                className: clsx(
                  classes.subtleTitle,
                  isActive && classes.activeTitle
                )
              }}
            />
          </ListItem>
        );
      }

      const isActiveMenu = hasActiveDescendant(item, location.pathname);

      return (
        <div
          key={item.key}
          // abre / fecha no hover, não no clique
          onMouseEnter={() => setOpen(item.key, true)}
          onMouseLeave={() => setOpen(item.key, false)}
        >
          <ListItem
            button
            className={levelClass}
          >
            <ListItemIcon className={clsx(
              classes.icon,
              isActiveMenu && classes.activeIcon
            )}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                className: clsx(
                  classes.subtleTitle,
                  isActiveMenu && classes.activeTitle
                )
              }}
            />

            {isOpen(item.key) ? (
              <ExpandLessRounded
                className={clsx(isActiveMenu && classes.activeIcon)}
              />
            ) : (
              <ExpandMoreRounded
                className={clsx(isActiveMenu && classes.activeIcon)}
              />
            )}
          </ListItem>

          <Collapse
            in={isOpen(item.key)}
            timeout={600}
            unmountOnExit
          >
            <List
              component="div"
              disablePadding
              className={classes.itemMenuFade}
            >
              {item.children && item.children.length > 0 ? (
                renderItems(item.children, level + 1)
              ) : (
                <ListItem className={levelClass}>
                  <ListItemText
                    primary="Nenhuma opção disponível"
                    primaryTypographyProps={{ className: classes.emptyMessage }}
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </div>
      );
    });
  };

  return (
    <List>
      {isPartnerFixedMenu ? renderItems(finalMenu) : (
        loading ? (
          <ListItem>
            <ListItemText
              primary="Carregando permissões..."
            />
          </ListItem>
        ) : (
          renderItems(finalMenu)
        )
      )}
    </List>
  );
};

export default Sidebar;